import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { useOnboarding } from "./hooks/useOnboarding";
import { AuthModalProvider, useAuthModal } from "./hooks/useAuthModal";
import { AuthProvider } from "./hooks/useAuth";
import { AuthModal } from "./components/AuthModal";

// Eagerly loaded pages (no lazy loading for instant navigation)
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Macros from "./pages/Macros";
import MacroDetail from "./pages/MacroDetail";
import Submit from "./pages/Submit";
import Dashboard from "./pages/Dashboard";
import Edit from "./pages/Edit";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import SettingsAccount from "./pages/settings/Account";
import SettingsPreferences from "./pages/settings/Preferences";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import DMCA from "./pages/DMCA";
import Docs from "./pages/Docs";
import About from "./pages/About";
import Contact from "./pages/Contact";
import ComingSoon from "./pages/ComingSoon";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();


const OnboardingShortcut = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetOnboarding } = useOnboarding();

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ctrl+Shift+R to reset onboarding
      if (e.ctrlKey && e.shiftKey && e.key === 'R') {
        e.preventDefault();
        resetOnboarding();
        
        // Navigate to vault if not already there
        if (location.pathname !== '/vault') {
          navigate('/vault');
        } else {
          // Reload the page to restart the tour on the vault page
          window.location.reload();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, location, resetOnboarding]);

  return null;
};

const AuthModalWrapper = () => {
  const { isOpen, closeAuthModal, onSuccessCallback, mode } = useAuthModal();
  return (
    <AuthModal
      open={isOpen}
      onOpenChange={closeAuthModal}
      onSuccess={onSuccessCallback}
      mode={mode}
    />
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <TooltipProvider>
          <AuthProvider>
            <AuthModalProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <OnboardingShortcut />
                <AuthModalWrapper />
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/vault" element={<Macros />} />
                  <Route path="/m/:slug" element={<MacroDetail />} />
                  <Route path="/upload" element={<Submit />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/edit/:slug" element={<Edit />} />
                  <Route path="/profile/:username" element={<Profile />} />
                  <Route path="/settings" element={<Settings />}>
                    <Route path="account" element={<SettingsAccount />} />
                    <Route path="preferences" element={<SettingsPreferences />} />
                  </Route>
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/docs" element={<Docs />} />
                  <Route path="/dmca" element={<DMCA />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/blog" element={<ComingSoon />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </AuthModalProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
